import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertBanner from "./AlertBanner";
import PrimaryButton from "../buttons/primaryButton";
import { call as rpc } from "../../../common/jsonRpcRoutes";

interface RewardsSignInSectionProps {
  /** Booking email. The account is already known to exist for this address. */
  readonly email: string;
  /** Called with the signed-in guest's balance (when known) on success. */
  readonly onSignInSuccess: (pointsBalance?: number) => void;
}

/**
 * Inline "sign in to see your points" form shown on the confirmation.
 *
 * A guest whose email already has a DesignMyNight account earns the points
 * without doing anything — Collins resolves the reward user from the booking
 * email — but they cannot see their balance, and the rewards link would open
 * logged out. Signing in here fixes both: the server hands the session to
 * designmynight.com on success, so the link opens authenticated.
 *
 * Deliberately mirrors its sibling `RewardsSignUpSection`, and reuses the same
 * `/loginWithEmail` call and translation keys as the login on the customer
 * details step, so the app has a single login behaviour.
 */
export default function RewardsSignInSection({
  email,
  onSignInSuccess,
}: RewardsSignInSectionProps): React.ReactElement {
  const { t } = useTranslation();
  const passwordRef = useRef("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSignIn = useCallback(async () => {
    const password = passwordRef.current;
    if (!password) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const result = await rpc("/loginWithEmail", { email, password });
      if (result.success && result.user) {
        onSignInSuccess(result.user.points_balance);
      } else {
        setLoginError(result.error ?? t("login_error", "Invalid email or password. Please try again."));
      }
    } catch {
      setLoginError(t("login_failed", "Login failed. Please try again."));
    } finally {
      setLoginLoading(false);
    }
  }, [email, onSignInSuccess, t]);

  return (
    <section
      aria-label={t("login_section", "Account detected")}
      className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-white gcw:p-bs-3 gcw:mb-bs-3"
    >
      <p className="gcw:text-body gcw:text-dark-blue gcw:font-semibold">
        {/*
          Reuses the prompt already shown pre-booking ("Log in or sign up after
          booking to claim your reward points") — this is that moment.
        */}
        {t("booking_rewards_login_prompt")}
      </p>
      <div className="gcw:w-full gcw:mt-4">
        <label
          htmlFor="rewards-sign-in-password"
          className="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2 gcw:block"
        >
          {t("password", "Password")}
        </label>
        <input
          id="rewards-sign-in-password"
          type="password"
          placeholder={t("password", "Password")}
          className="container-inputs gcw:w-full gcw:md:w-1/2"
          onChange={(e) => {
            passwordRef.current = e.target.value;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loginLoading) {
              e.preventDefault();
              void handleSignIn();
            }
          }}
          aria-describedby={loginError ? "rewards-sign-in-error" : undefined}
        />
      </div>
      {loginError && (
        <div id="rewards-sign-in-error" className="gcw:mt-2">
          <AlertBanner variant="danger">
            <span role="alert">{loginError}</span>
          </AlertBanner>
        </div>
      )}
      <div className="gcw:mt-4">
        <PrimaryButton
          text={t("booking_login", "Log In")}
          type="button"
          onClick={() => void handleSignIn()}
          disabled={loginLoading}
          loading={loginLoading}
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-pill-lg gcw:bg-dark-purple gcw:text-white gcw:border gcw:border-dark-purple gcw:px-bs-3 gcw:py-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:hover:cursor-pointer"
        />
      </div>
    </section>
  );
}
